<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
});

const axios = window.axios;

const { t } = useI18n();
const store = useStore();
const accountId = computed(() => store.getters.getCurrentAccountId);

const status = ref(null);
let pollInterval = null;

const hasStatus = computed(
  () => status.value && status.value.status !== 'not_found'
);

const statusClasses = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return 'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800';
    case 'completed':
      return 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800';
    case 'failed':
      return 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800';
    default:
      return 'bg-slate-50 border-slate-200 dark:bg-slate-900/20 dark:border-slate-800';
  }
});

const statusIcon = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return 'arrow-sync-circle';
    case 'completed':
      return 'checkmark-circle';
    case 'failed':
      return 'dismiss-circle';
    default:
      return 'info';
  }
});

const iconClasses = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return 'text-blue-600 dark:text-blue-400 animate-spin';
    case 'completed':
      return 'text-green-600 dark:text-green-400';
    case 'failed':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-slate-600 dark:text-slate-400';
  }
});

const statusTitle = computed(() => {
  switch (status.value?.status) {
    case 'running':
      return t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.STATUS_RUNNING');
    case 'completed':
      return t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.STATUS_COMPLETED');
    case 'failed':
      return t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.STATUS_FAILED');
    default:
      return t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.STATUS_UNKNOWN');
  }
});

const statusMessage = computed(() => {
  if (status.value?.status === 'running') {
    return t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.MERGING_IN_PROGRESS');
  }
  if (status.value?.status === 'completed') {
    if (status.value.merged_count === 0) {
      return (
        status.value.message ||
        t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.NO_DUPLICATES')
      );
    }
    return t(
      'API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.MERGE_COMPLETED_SUCCESS'
    );
  }
  if (status.value?.status === 'failed') {
    return (
      status.value.error ||
      t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.MERGE_FAILED')
    );
  }
  return '';
});

const progressSummary = computed(() => {
  return `${t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.PROGRESS')}: ${status.value?.processed_contacts || 0}/${status.value?.total_contacts || 0}`;
});

const progressPercent = computed(() => `${status.value?.progress || 0}%`);

const mergedCountLabel = computed(
  () => `${t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.MERGED_COUNT')}:`
);

const totalContactsLabel = computed(
  () => `${t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.TOTAL_CONTACTS')}:`
);

const startedAtLabel = computed(
  () => `${t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.STARTED_AT')}:`
);

const finishedAtLabel = computed(
  () => `${t('API_CONVERSATIONS_MERGE.MERGE_CONVERSATIONS.FINISHED_AT')}:`
);

const formatTime = time => {
  if (!time) return '';
  const date = new Date(time);
  return date.toLocaleString();
};

const stopPolling = () => {
  if (pollInterval) {
    clearInterval(pollInterval);
    pollInterval = null;
  }
};

const fetchStatus = async () => {
  if (!accountId.value) return;

  try {
    const response = await axios.get(
      `/api/v1/accounts/${accountId.value}/inboxes/${props.inboxId}/merge_conversations/status`
    );
    status.value = response.data;

    if (
      status.value.status === 'completed' ||
      status.value.status === 'failed'
    ) {
      stopPolling();
    }
  } catch {
    // Silent fail
  }
};

const startPolling = () => {
  fetchStatus();
  pollInterval = setInterval(fetchStatus, 3000);
};

const dismiss = () => {
  status.value = null;
};

onMounted(() => {
  startPolling();
});

onUnmounted(() => {
  stopPolling();
});
</script>

<template>
  <div>
    <div
      v-if="hasStatus"
      class="mb-4 rounded-lg border p-4"
      :class="statusClasses"
    >
      <div class="flex items-start justify-between">
        <div class="flex-1">
          <div class="flex items-center gap-2">
            <FluentIcon
              :icon="statusIcon"
              class="h-5 w-5"
              :class="iconClasses"
            />
            <h3 class="font-semibold text-slate-900 dark:text-slate-100">
              {{ statusTitle }}
            </h3>
          </div>

          <p class="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {{ statusMessage }}
          </p>

          <div v-if="status.status === 'running'" class="mt-3">
            <div
              class="flex items-center justify-between text-xs text-slate-600 dark:text-slate-400 mb-1"
            >
              <span>{{ progressSummary }}</span>
              <span>{{ progressPercent }}</span>
            </div>
            <div
              class="h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden"
            >
              <div
                class="h-full bg-woot-500 transition-all duration-300"
                :style="{ width: `${status.progress || 0}%` }"
              />
            </div>
          </div>

          <div
            v-if="status.status === 'completed' && status.merged_count > 0"
            class="mt-3 flex gap-4 text-sm"
          >
            <div>
              <span class="text-slate-600 dark:text-slate-400">{{
                mergedCountLabel
              }}</span>
              <span
                class="ml-1 font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ status.merged_count }}
              </span>
            </div>
            <div>
              <span class="text-slate-600 dark:text-slate-400">{{
                totalContactsLabel
              }}</span>
              <span
                class="ml-1 font-semibold text-slate-900 dark:text-slate-100"
              >
                {{ status.total_contacts }}
              </span>
            </div>
          </div>

          <p
            v-if="status.started_at || status.finished_at"
            class="mt-2 text-xs text-slate-500 dark:text-slate-500"
          >
            <template v-if="status.status === 'running'">
              {{ startedAtLabel }}
              {{ formatTime(status.started_at) }}
            </template>
            <template v-else>
              {{ finishedAtLabel }}
              {{ formatTime(status.finished_at) }}
            </template>
          </p>
        </div>

        <button
          v-if="status.status !== 'running'"
          class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 ml-4"
          @click="dismiss"
        >
          <FluentIcon icon="dismiss" class="h-5 w-5" />
        </button>
      </div>
    </div>
  </div>
</template>
